// texe-web/src/lib/ranks.ts
// Mobile'dan adapt edildi - Web için rank utilities

// Global rank config cache
let rankConfigCache: {
  ranks: Array<{ level: number; name: string; minTP: number; icon: string }>;
  customThresholds: { [level: number]: number } | null;
  tpMultiplier: number;
  lastFetch: number;
} | null = null;

const CACHE_TTL = 5 * 60 * 1000; // 5 dakika cache

/**
 * Backend'den rank config'i çek
 */
async function fetchRankConfig() {
  try {
    const response = await fetch('/api/savwar/rank-config');
    const result = await response.json();
    
    if (result.success && result.data) {
      rankConfigCache = {
        ranks: result.data.ranks,
        customThresholds: result.data.customThresholds,
        tpMultiplier: result.data.tpMultiplier,
        lastFetch: Date.now(),
      };
    }
  } catch (error) {
    console.error('Failed to fetch rank config:', error);
  }
}

/**
 * Rank config'i al (cache varsa kullan)
 */
async function getRankConfig() {
  if (!rankConfigCache || Date.now() - rankConfigCache.lastFetch > CACHE_TTL) {
    await fetchRankConfig();
  }
  return rankConfigCache;
}

/**
 * Config'in yüklendiğinden emin ol (component'ler için)
 */
export async function ensureRankConfigLoaded() {
  if (!rankConfigCache) {
    await fetchRankConfig();
  }
  return rankConfigCache;
}

// Sayfa yüklendiğinde config'i çek
fetchRankConfig();

// Rütbe icon'ları (Web için public path)
export const RANK_ICONS: Record<number, string> = {
  1: '/ranks/onbasi.png',     // Er (icon yok, onbaşı kullanıyoruz)
  2: '/ranks/onbasi.png',     // Onbaşı
  3: '/ranks/cavus.png',      // Çavuş
  4: '/ranks/uzmanonbasi.png', // Uzman Onbaşı
  5: '/ranks/uzmancavus.png', // Uzman Çavuş
  6: '/ranks/astsubaycavus.png', // Astsubay Çavuş
  7: '/ranks/astsubaykidemlicavus.png', // Astsubay Kıdemli Çavuş
  8: '/ranks/astsubayustcavus.png', // Astsubay Üstçavuş
  9: '/ranks/astsubaykidemliustcavus.png', // Astsubay Kıdemli Üstçavuş
  10: '/ranks/astsubaybascavus.png', // Astsubay Başçavuş
  11: '/ranks/astsubaykidemlibascavus.png', // Astsubay Kıdemli Başçavuş
  12: '/ranks/astegmen.png',  // Asteğmen
  13: '/ranks/tegmen.png',    // Teğmen
  14: '/ranks/usttegmen.png', // Üsteğmen
  15: '/ranks/yuzbasi.png',   // Yüzbaşı
  16: '/ranks/binbasi.png',   // Binbaşı
  17: '/ranks/yarbay.png',    // Yarbay
  18: '/ranks/albay.png',     // Albay
  19: '/ranks/tuggeneral.png', // Tuğgeneral
  20: '/ranks/tumgeneral.png', // Tümgeneral
  21: '/ranks/korgeneral.png', // Korgeneral
  22: '/ranks/orgeneral.png', // Orgeneral
  23: '/ranks/maresal.png',   // Mareşal
};

// Rütbe ismi mapping (TSK sıralaması)
export const RANK_NAMES: Record<number, string> = {
  1: 'Er',
  2: 'Onbaşı', 
  3: 'Çavuş',
  4: 'Uzman Onbaşı',
  5: 'Uzman Çavuş',
  6: 'Astsubay Çavuş',
  7: 'Astsubay Kıdemli Çavuş',
  8: 'Astsubay Üstçavuş', 
  9: 'Astsubay Kıdemli Üstçavuş',
  10: 'Astsubay Başçavuş',
  11: 'Astsubay Kıdemli Başçavuş',
  12: 'Asteğmen',
  13: 'Teğmen',
  14: 'Üsteğmen',
  15: 'Yüzbaşı',
  16: 'Binbaşı',
  17: 'Yarbay',
  18: 'Albay',
  19: 'Tuğgeneral',
  20: 'Tümgeneral',
  21: 'Korgeneral',
  22: 'Orgeneral',
  23: 'Mareşal',
};

// Rütbe renkleri (CSS class için)
export const RANK_COLORS: Record<number, string> = {
  1: 'bg-gray-600',      // Er
  2: 'bg-gray-500',      // Onbaşı
  3: 'bg-blue-600',      // Çavuş
  4: 'bg-green-600',     // Uzman Onbaşı
  5: 'bg-green-500',     // Uzman Çavuş
  6: 'bg-purple-600',    // Astsubay Çavuş
  7: 'bg-purple-500',    // Astsubay Kıdemli Çavuş
  8: 'bg-purple-400',    // Astsubay Üstçavuş
  9: 'bg-indigo-600',    // Astsubay Kıdemli Üstçavuş
  10: 'bg-indigo-500',   // Astsubay Başçavuş
  11: 'bg-indigo-400',   // Astsubay Kıdemli Başçavuş
  12: 'bg-yellow-600',   // Asteğmen
  13: 'bg-yellow-500',   // Teğmen
  14: 'bg-orange-600',   // Üsteğmen
  15: 'bg-orange-500',   // Yüzbaşı
  16: 'bg-red-600',      // Binbaşı
  17: 'bg-red-500',      // Yarbay
  18: 'bg-red-400',      // Albay
  19: 'bg-pink-600',     // Tuğgeneral
  20: 'bg-pink-500',     // Tümgeneral
  21: 'bg-rose-600',     // Korgeneral
  22: 'bg-rose-500',     // Orgeneral
  23: 'bg-gradient-to-r from-yellow-400 to-red-600', // Mareşal
};

/**
 * Rütbe seviyesinden rütbe adını getir
 */
export function getRankName(level: number): string {
  return RANK_NAMES[level] || `Bilinmeyen (${level})`;
}

/**
 * Rütbe seviyesinden CSS class getir
 */
export function getRankColor(level: number): string {
  return RANK_COLORS[level] || 'bg-gray-500';
}

/**
 * Er rütbesi kontrolü (özel gösterim)
 */
export function isErRank(level: number): boolean {
  return level === 1;
}

/**
 * Astsubay kontrolü
 */
export function isAstsubay(level: number): boolean {
  return level >= 6 && level <= 11;
}

/**
 * Subay kontrolü
 */
export function isSubay(level: number): boolean {
  return level >= 12 && level <= 18;
}

/**
 * General kontrolü
 */
export function isGeneral(level: number): boolean {
  return level >= 19 && level <= 23;
}

/**
 * Rütbe kategorisi getir
 */
export function getRankCategory(level: number): string {
  if (level >= 1 && level <= 5) return 'Erbaş/Uzman';
  if (level >= 6 && level <= 11) return 'Astsubay';
  if (level >= 12 && level <= 18) return 'Subay';
  if (level >= 19 && level <= 23) return 'General';
  return 'Bilinmeyen';
}

/**
 * Rütbe icon'u getir (web path)
 */
export function getRankIcon(level: number): string {
  return RANK_ICONS[level] || '/ranks/onbasi.png';
}

/**
 * Rütbe emoji'si getir
 */
export function getRankEmoji(level: number): string {
  if (level >= 1 && level <= 3) return '🟢'; // Erbaş
  if (level >= 4 && level <= 5) return '🔵'; // Uzman
  if (level >= 6 && level <= 11) return '🟣'; // Astsubay
  if (level >= 12 && level <= 15) return '🟡'; // Genç Subay
  if (level >= 16 && level <= 18) return '🔴'; // Kıdemli Subay
  if (level >= 19 && level <= 23) return '⭐'; // General
  return '❓';
}

/**
 * TP'den rütbe hesapla (backend ile uyumlu)
 * Backend'deki custom thresholds'ları kullanır
 */
export function calculateRankFromTP(tp: number): number {
  // Cache'den config al
  const config = rankConfigCache;
  
  if (!config || !config.ranks) {
    // Fallback: basit hesaplama
    return Math.max(1, Math.min(23, Math.floor(tp) + 1));
  }

  // Custom thresholds varsa kullan
  if (config.customThresholds && Object.keys(config.customThresholds).length > 0) {
    let matchedLevel = 1;
    
    // TP'ye göre uygun seviyeyi bul (büyükten küçüğe doğru kontrol et)
    for (let i = config.ranks.length - 1; i >= 0; i--) {
      const rank = config.ranks[i];
      const threshold = config.customThresholds[rank.level] ?? rank.minTP;
      if (tp >= threshold) {
        matchedLevel = rank.level;
        break;
      }
    }
    
    return matchedLevel;
  }

  // Default sistem: minTP'ye göre
  let rankLevel = 1;
  for (let i = config.ranks.length - 1; i >= 0; i--) {
    if (tp >= config.ranks[i].minTP) {
      rankLevel = config.ranks[i].level;
      break;
    }
  }
  
  return Math.max(1, Math.min(23, rankLevel));
}

/**
 * Tam rütbe bilgisini al
 */
export interface RankInfo {
  level: number;
  name: string;
  minTP: number;
  emoji: string;
  color: string;
  icon: string;
}

export function getRankInfo(rank: number): RankInfo {
  const config = rankConfigCache;
  
  if (!config || !config.ranks) {
    // Fallback
    return {
      level: 1,
      name: 'Er',
      minTP: 0,
      emoji: getRankEmoji(1),
      color: getRankColor(1),
      icon: getRankIcon(1),
    };
  }

  const rankInfo = config.ranks.find(r => r.level === rank) || config.ranks[0];
  
  // Custom threshold varsa onu kullan, yoksa default minTP
  const effectiveMinTP = (config.customThresholds && config.customThresholds[rankInfo.level] !== undefined)
    ? config.customThresholds[rankInfo.level]
    : rankInfo.minTP;
  
  return {
    level: rankInfo.level,
    name: rankInfo.name,
    minTP: effectiveMinTP,
    emoji: getRankEmoji(rankInfo.level),
    color: getRankColor(rankInfo.level),
    icon: getRankIcon(rankInfo.level),
  };
}

/**
 * TP'ye göre tam rütbe bilgisini al
 */
export function getRankInfoFromTP(tp: number): RankInfo {
  const rank = calculateRankFromTP(tp);
  return getRankInfo(rank);
}

/**
 * Bir sonraki rütbeye ne kadar TP kaldığını hesapla
 */
export function getTPToNextRank(currentTP: number): { 
  nextRank: RankInfo | null; 
  tpNeeded: number;
  progress: number;
} {
  const currentRank = calculateRankFromTP(currentTP);
  const config = rankConfigCache;
  
  if (!config || !config.ranks || currentRank >= 23) {
    return { nextRank: null, tpNeeded: 0, progress: 100 }; // Maksimum rütbe
  }
  
  const nextRankData = config.ranks.find(r => r.level === currentRank + 1);
  if (!nextRankData) {
    return { nextRank: null, tpNeeded: 0, progress: 100 };
  }
  
  const nextRank = getRankInfo(currentRank + 1);
  const currentRankInfo = getRankInfo(currentRank);
  
  const tpNeeded = nextRank.minTP - currentTP;
  const tpRange = nextRank.minTP - currentRankInfo.minTP;
  const progress = tpRange > 0 ? ((currentTP - currentRankInfo.minTP) / tpRange) * 100 : 0;
  
  return { nextRank, tpNeeded, progress: Math.min(100, Math.max(0, progress)) };
}

/**
 * Tüm rütbeler listesi
 */
export const ALL_RANKS: RankInfo[] = Array.from({ length: 23 }, (_, i) => getRankInfo(i + 1));